import { create } from 'zustand';
import type { BloodHoundQuery, ADNodeData, ADEdgeData, BloodHoundQueryResponse } from '../services/adApi';

export interface ADGraphNode {
  id: string;
  label: string;
  kind: string;
  kinds: string[];
  objectId: string;
  isTierZero: boolean;
  isOwnedObject: boolean;
  lastSeen: string;
  properties: Record<string, any>;
}

export interface ADGraphEdge {
  id: string;
  source: string;
  target: string;
  label: string;
  kind: string;
  lastSeen: string;
  properties: Record<string, any>;
}

interface ADStore {
  // Tenant
  tenants: { tenantName: string }[];
  selectedTenant: string | null;
  setTenants: (tenants: { tenantName: string }[]) => void;
  setSelectedTenant: (tenant: string) => void;

  // Queries
  queries: BloodHoundQuery[];
  selectedQuery: BloodHoundQuery | null;
  isLoadingQueries: boolean;
  setQueries: (queries: BloodHoundQuery[]) => void;
  setSelectedQuery: (query: BloodHoundQuery | null) => void;
  setLoadingQueries: (loading: boolean) => void;

  // Graph data (raw from API)
  graphNodes: ADGraphNode[];
  graphEdges: ADGraphEdge[];
  isLoadingGraph: boolean;
  setGraphData: (nodes: Record<string, ADNodeData>, edges: ADEdgeData[]) => void;
  loadGraph: (request: (signal: AbortSignal) => Promise<BloodHoundQueryResponse | undefined>) => Promise<boolean>;
  clearGraph: () => void;
  setLoadingGraph: (loading: boolean) => void;

  // Selection
  selectedNode: ADGraphNode | null;
  selectNode: (node: ADGraphNode | null) => void;

  // Theme
  theme: 'light' | 'dark';
  toggleTheme: () => void;
}

let graphController: AbortController | null = null;

export const useADStore = create<ADStore>((set, get) => ({
  tenants: [],
  selectedTenant: null,
  setTenants: (tenants) => set({ tenants }),
  setSelectedTenant: (tenant) => set({ selectedTenant: tenant }),

  queries: [],
  selectedQuery: null,
  isLoadingQueries: false,
  setQueries: (queries) => set({ queries }),
  setSelectedQuery: (query) => set({ selectedQuery: query }),
  setLoadingQueries: (loading) => set({ isLoadingQueries: loading }),

  graphNodes: [],
  graphEdges: [],
  isLoadingGraph: false,
  setGraphData: (nodes, edges) => {
    const graphNodes: ADGraphNode[] = Object.entries(nodes).map(([id, node]) => ({
      id,
      label: node.label,
      kind: node.kind,
      kinds: node.kinds || [],
      objectId: node.objectId,
      isTierZero: node.isTierZero,
      isOwnedObject: node.isOwnedObject,
      lastSeen: node.lastSeen,
      properties: node.properties || {},
    }));

    const graphEdges: ADGraphEdge[] = edges.map((edge, index) => ({
      id: `edge-${edge.source}-${edge.target}-${index}`,
      source: edge.source,
      target: edge.target,
      label: edge.label,
      kind: edge.kind,
      lastSeen: edge.lastSeen,
      properties: edge.properties || {},
    }));

    set({ graphNodes, graphEdges });
  },
  loadGraph: async (request) => {
    graphController?.abort();
    const controller = new AbortController();
    graphController = controller;
    set({ isLoadingGraph: true });
    try {
      const response = await request(controller.signal);
      if (graphController !== controller) return false;
      get().setGraphData(response?.data?.nodes ?? {}, response?.data?.edges ?? []);
      return true;
    } catch (error) {
      if (graphController !== controller) return false;
      throw error;
    } finally {
      if (graphController === controller) {
        graphController = null;
        set({ isLoadingGraph: false });
      }
    }
  },
  clearGraph: () => {
    graphController?.abort();
    graphController = null;
    set({
      graphNodes: [],
      graphEdges: [],
      selectedNode: null,
      selectedQuery: null,
      isLoadingGraph: false,
    });
  },
  setLoadingGraph: (loading) => set({ isLoadingGraph: loading }),

  selectedNode: null,
  selectNode: (node) => set({ selectedNode: node }),

  theme: 'dark',
  toggleTheme: () => {
    const newTheme = get().theme === 'light' ? 'dark' : 'light';
    set({ theme: newTheme });
    if (newTheme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  },
}));
